import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, CalendarDays, Leaf, Menu, MessageCircle, Phone, X } from "lucide-react";
import EnquiryModal from "./EnquiryModal";
import { ZORBA, BRAND, whatsappLink, ANALYTICS_EVENTS } from "../data/content";

const track = (name, detail = {}) =>
  window.dispatchEvent(new CustomEvent("spa-analytics", { detail: { name, ...detail } }));

function Header({ onBook }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);
  const links = [
    { to: "/spa-services-bhubaneswar/", label: "Services", testid: "nav-services-link" },
    { to: "/areas/", label: "Location", testid: "nav-areas-link" },
    { to: "/guides/", label: "Guides", testid: "nav-guides-link" },
    { to: "/zorba-international-spa/", label: "Featured spa", testid: "nav-zorba-link" },
    { to: "/contact/", label: "Contact", testid: "nav-contact-link" },
  ];
  return (
    <header className="site-header">
      <div className="nav-shell">
        <Link to="/" className="brand" data-testid="brand-link" onClick={() => setOpen(false)}>
          <span className="brand-mark"><Leaf size={17} /></span>
          <span>SPA NEAR ME<small>BHUBANESWAR</small></span>
        </Link>
        <nav className={open ? "nav-links open" : "nav-links"} data-testid="main-navigation">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={l.testid}>{l.label}</Link>
          ))}
          <button className="btn btn-primary nav-book" onClick={onBook} data-testid="header-book-button">
            Book an enquiry <ArrowRight size={16} />
          </button>
        </nav>
        <button
          className="icon-btn menu-toggle"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Close navigation" : "Open navigation"}
          data-testid="mobile-menu-button"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>
    </header>
  );
}

function Footer({ onBook }) {
  const year = new Date().getFullYear();
  return (
    <footer className="site-footer">
      <div className="footer-grid">
        <div>
          <div className="brand footer-brand">
            <span className="brand-mark"><Leaf size={17} /></span>
            <span>SPA NEAR ME<small>BHUBANESWAR</small></span>
          </div>
          <p>{BRAND.description}</p>
          <p className="muted-small">Featured partner: {ZORBA.name} — {ZORBA.city}, {ZORBA.state}.</p>
        </div>
        <div>
          <strong>Explore</strong>
          <Link to="/spa-in-bhubaneswar/" data-testid="footer-spa-in-bhubaneswar">Spa in Bhubaneswar</Link>
          <Link to="/massage-in-bhubaneswar/" data-testid="footer-massage-in-bhubaneswar">Massage in Bhubaneswar</Link>
          <Link to="/spa-services-bhubaneswar/" data-testid="footer-services-link">Spa services</Link>
          <Link to="/spa-packages-bhubaneswar/" data-testid="footer-packages-link">Spa packages</Link>
          <Link to="/spa-prices-bhubaneswar/" data-testid="footer-prices-link">Spa prices</Link>
          <Link to="/spa-offers-bhubaneswar/" data-testid="footer-offers-link">Spa offers</Link>
        </div>
        <div>
          <strong>Nearby</strong>
          <Link to="/areas/patia/" data-testid="footer-patia">Patia & KIIT</Link>
          <Link to="/areas/jaydev-vihar/" data-testid="footer-jaydev-vihar">Jaydev Vihar</Link>
          <Link to="/areas/nayapalli/" data-testid="footer-nayapalli">Nayapalli</Link>
          <Link to="/areas/chandrasekharpur/" data-testid="footer-chandrasekharpur">Chandrasekharpur</Link>
          <Link to="/areas/bhubaneswar-airport/" data-testid="footer-airport">Airport area</Link>
          <Link to="/areas/" data-testid="footer-areas-link">All areas</Link>
        </div>
        <div>
          <strong>Partner & info</strong>
          <Link to="/zorba-international-spa/" data-testid="footer-zorba-link">{ZORBA.name}</Link>
          <Link to="/zorba-contact/" data-testid="footer-zorba-contact">Partner contact</Link>
          <button onClick={onBook} data-testid="footer-book-button">Booking enquiry</button>
          <Link to="/gift-vouchers/" data-testid="footer-gift-vouchers-link">Gift vouchers</Link>
          <Link to="/share-your-experience/" data-testid="footer-share-experience-link">Share your experience</Link>
          <Link to="/guides/" data-testid="footer-guides-link">Wellness guides</Link>
          <Link to="/contact/" data-testid="footer-contact-link">Contact us</Link>
          <Link to="/privacy-policy/" data-testid="footer-privacy-link">Privacy policy</Link>
          <Link to="/terms/" data-testid="footer-terms-link">Terms & conditions</Link>
          <Link to="/cancellation-policy/" data-testid="footer-cancellation-link">Cancellation policy</Link>
          <Link to="/booking-policy/" data-testid="footer-booking-policy-link">Booking policy</Link>
          <Link to="/disclaimer/" data-testid="footer-disclaimer-link">Disclaimer</Link>
        </div>
      </div>
      <div className="footer-bottom">
        <span>© {year} {BRAND.name}. All rights reserved.</span>
        <span>Discovery and enquiry platform — not an official directory of Google or any public authority.</span>
      </div>
    </footer>
  );
}

function MobileCTA({ onBook }) {
  const callDisabled = !ZORBA.phone;
  return (
    <div className="mobile-cta" role="group" aria-label="Quick contact">
      <button
        className={callDisabled ? "pending-call" : "mobile-call"}
        disabled={callDisabled}
        title={callDisabled ? "Phone details will be added when supplied" : "Call Zorba International Spa"}
        data-testid="mobile-call-cta"
        onClick={() => {
          if (!callDisabled) {
            track(ANALYTICS_EVENTS.callClick, { source: "mobile_sticky" });
            window.location.href = `tel:${ZORBA.phone}`;
          }
        }}
      >
        <Phone size={17} />Call
      </button>
      <a
        className="mobile-whatsapp"
        href={whatsappLink()}
        target="_blank"
        rel="noreferrer"
        data-testid="mobile-whatsapp-cta"
        onClick={() => track(ANALYTICS_EVENTS.whatsappClick, { source: "mobile_sticky" })}
      >
        <MessageCircle size={17} />WhatsApp
      </a>
      <button onClick={onBook} data-testid="mobile-book-cta">
        <CalendarDays size={17} />Book
      </button>
    </div>
  );
}

export default function Layout({ children }) {
  const [modal, setModal] = useState(null);
  const open = (service = "General spa enquiry") => {
    setModal(service);
    track(ANALYTICS_EVENTS.bookOpen, { service });
  };
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);
  return (
    <div className="App">
      <Header onBook={() => open()} />
      <main>{typeof children === "function" ? children(open) : children}</main>
      <Footer onBook={() => open()} />
      {modal && <EnquiryModal service={modal} onClose={() => setModal(null)} />}
      <MobileCTA onBook={() => open()} />
    </div>
  );
}

export { Header, Footer, MobileCTA };
